import React, { useEffect, useState } from "react";

import {
  MinusCircleIcon,
  PlusCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { getTenantDepartmentsData } from "@services/API/Requests/TenantDepartmentRequests";
import LoadingIcon from "@common/LoadingIcon";
import { USERTENANT } from "@types/usertenant";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { error_message } from "@constants/Errors";
import { APP_NAMES } from "@constants/Enums";
import PaginationControls from "@services/PaginationControls";

interface DepartmentModalProps {
  isOpen: boolean;
  title?: string;
  onRequestClose: () => void;
  onSelectGroup: (department: USERTENANT) => void;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({
  isOpen,
  title = "部署選択",
  onRequestClose,
  onSelectGroup,
}) => {
  const [tenantDepartment, setTenantDepartments] = useState<never[]>([]);
  const [originalTenantDepartments, setOriginalTenantDepartments] = useState<
    never[]
  >([]);
  const availableApps = useSelector((state) => state.apps.availableApps);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [totalTenantDepartments, setTotalTenantDepartments] =
    useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const totalPages: number = Math.ceil(totalTenantDepartments / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1, itemsPerPage);
  }, [itemsPerPage]);

  if (title === null) {
    title = "部署選択";
  }

  const returnId = (department) => {
    onSelectGroup(department);
  };

  const handlePageChange = (newPage: number) => {
    fetchData(newPage, itemsPerPage);
    setCurrentPage(newPage);
  };
  const fetchData = async (page: number, perPage: number) => {
    try {
      setLoading(true);
      await getTenantDepartmentsData(
        setTenantDepartments,
        setOriginalTenantDepartments,
        setLoading,
        setError,
        setTotalTenantDepartments,
        page,
        perPage,
      );
    } catch (err) {
      setError(error_message.departments.load_failed);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData(currentPage, itemsPerPage);
    }
  }, [isOpen]);

  const buildTenantTree = (departments) => {
    const map = {};
    const roots = [];

    departments.forEach((dept) => {
      map[dept.id] = { ...dept, children: [] };
    });

    departments.forEach((dept) => {
      if (dept.parent === null) {
        roots.push(map[dept.id]);
      } else {
        if (map[dept.parent]) {
          map[dept.parent].children.push(map[dept.id]);
        }
      }
    });

    return roots;
  };

  const DepartmentTree = ({ depts }) => {
    const [expandedNodes, setExpandedNodes] = useState({});

    const toggleExpand = (id) => {
      setExpandedNodes((prevState) => ({
        ...prevState,
        [id]: !prevState[id],
      }));
    };

    const displayTree = (nodes) => {
      return (
        <ul>
          {nodes.map((node) => (
            <li key={node.id} className="node-item">
              <div className="flex justify-between items-center">
                {node.children.length !== 0 ? (
                  <div
                    className="flex justify-normal items-center"
                    onClick={() => toggleExpand(node.id)}
                  >
                    {expandedNodes[node.id] ? (
                      <MinusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    ) : (
                      <PlusCircleIcon className="mr-5 w-5 h-5 text-mayo-link-text hover:opacity-50" />
                    )}
                    <div className="underline hover:opacity-50 cursor-pointer text-mayo-light-gray text-xl">
                      {node.name}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-normal items-center">
                    <div className="text-xl text-mayo-light-gray">
                      {node.name}
                    </div>
                  </div>
                )}
                <button
                  className="button-small-white ml-2 items-end"
                  onClick={() => {
                    returnId(node);
                  }}
                >
                  選択
                </button>
              </div>
              <div className="divider mt-2"></div>
              {node.children &&
                node.children.length > 0 &&
                expandedNodes[node.id] && (
                  <div className="ml-8 mt-2">{displayTree(node.children)}</div>
                )}
            </li>
          ))}
        </ul>
      );
    };

    const treeData = buildTenantTree(depts);

    return <div>{displayTree(treeData)}</div>;
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center backdrop-brightness-50 z-[1000]">
      <div className="bg-white border border-text-field-border rounded-lg w-1/2 max-h-1/2 overflow-y-auto">
        <div className="mayo-card-header p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onRequestClose} className="text-xl hover:opacity-50">
            <svg
              className="ml-2 fill-body hover:fill-primary"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <XCircleIcon />
            </svg>
          </button>
        </div>
        {loading ? (
          <div className="mt-10">
            <LoadingIcon />
          </div>
        ) : tenantDepartment.length > 0 ? (
          <div className="mayo-card-body-modal overflow-y-auto max-h-[60vh] p-4">
            <DepartmentTree depts={tenantDepartment} />
          </div>
        ) : (
          <p className="flex items-center justify-center mt-5">
            {error_message.departments.no_data}
          </p>
        )}
        <div className="flex justify-end mb-5 mt-5 mr-2 sm:mr-4">
          <button
            onClick={onRequestClose}
            className="button-small-white ml-4 sm:ml-0"
          >
            {"キャンセル"}
          </button>
          {availableApps.includes(APP_NAMES.KAKELY) && (
            <Link to="/KAKELY/departments/settings/create/Master">
              <button className="button-small-white">{"部署登録に遷移"}</button>
            </Link>
          )}
        </div>
        <div className="pl-4 pr-4 pb-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalTenantDepartments}
            displayedItem={"部署"}
            onPageChange={handlePageChange}
            setPerPage={setItemsPerPage}
            perPage={itemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default DepartmentModal;
